import type { PipelineStage, StageGroup } from '../pipeline/types';
import { PIPELINE_STAGES } from '../pipeline/registry';
import { DEFAULT_CONTEXT_SOFT_LIMIT_TOKENS, isStageEffectivelyEnabled, type OptimizationSettings } from '../pipeline/settings';
import {
  saveOptimizationSettings,
  resetOptimizationSettingsToRecommended,
  getTaskTemplates,
  saveTaskTemplates,
  getContextTreesSettings,
  saveContextTreesSettings,
  DEFAULT_CONTEXTTREES_SETTINGS,
  type ContextTreesSettings,
} from '../db/settings-repo';
import type { PipelineRunReport } from '../pipeline/runner';
import type { AnthropicRequestBody, UsageInfo } from '../api/anthropic';
import { contextWindowFor } from '../api/pricing';
import { TEMPLATES_CHANGED_EVENT } from './events';
import { createSwitch } from './switch';

const GROUP_LABELS: Record<StageGroup, string> = {
  A: 'Context Selection',
  B: 'Code Minification',
  C: 'Diff-Based Workflows',
  D: 'Conversation Management',
  E: 'Prompt Hygiene',
};

export interface SessionStats {
  requests: number;
  tokensSentEstimate: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  estimatedCostUsd: number;
  baselineTokensEstimate: number;
}

export interface OptimizationPanelCallbacks {
  onSettingsChanged: (settings: OptimizationSettings) => void;
  /** "Trim context" button — close the least-recently-touched open files to bring context size back down. */
  onTrimContext: () => void;
}

/**
 * The right rail: what this session actually saved, then the pipeline itself as a statement of
 * priced line items — one row per stage, each showing what it saved on the most recent run and
 * carrying its own on/off switch under the group master switch.
 */
export class OptimizationPanel {
  private root: HTMLElement;
  private settings: OptimizationSettings;
  private callbacks: OptimizationPanelCallbacks;

  private budgetEl!: HTMLElement;
  private contextHealthEl!: HTMLElement;
  private dashboardEl!: HTMLElement;
  private inspectorEl!: HTMLDetailsElement;
  private groupsHost!: HTMLElement;
  private templatesHost!: HTMLElement;
  private contextTreesHost!: HTMLElement;
  private statementCountEl!: HTMLElement;
  private templates: Record<string, string> = {};
  private contextTrees: ContextTreesSettings = {
    ...DEFAULT_CONTEXTTREES_SETTINGS,
    reviewMode: { ...DEFAULT_CONTEXTTREES_SETTINGS.reviewMode },
  };
  private lastContextTokens = 0;
  /** Tokens each stage saved on the most recent run — shown per row, cleared to "—" until then. */
  private lastStageSaved: Record<string, number> = {};

  private groupEls = new Map<StageGroup, HTMLElement>();
  private groupCountEls = new Map<StageGroup, HTMLElement>();
  private stageRowEls = new Map<string, HTMLElement>();
  private stageSavedEls = new Map<string, HTMLElement>();

  constructor(container: HTMLElement, initialSettings: OptimizationSettings, callbacks: OptimizationPanelCallbacks) {
    this.root = container;
    this.settings = initialSettings;
    this.callbacks = callbacks;
    this.renderShell();
    this.renderGroups();
    void this.loadAndRenderTemplates();
    void this.loadAndRenderContextTrees();
  }

  private renderShell() {
    this.root.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'pane-header';
    header.innerHTML = '<span class="pane-title">Savings</span>';
    const resetBtn = document.createElement('button');
    resetBtn.className = 'btn subtle';
    resetBtn.textContent = 'Reset to recommended';
    resetBtn.addEventListener('click', () => void this.resetToRecommended());
    header.appendChild(resetBtn);
    this.root.appendChild(header);

    const panelBody = document.createElement('div');
    panelBody.className = 'rail-body';

    this.dashboardEl = document.createElement('div');
    this.dashboardEl.id = 'session-dashboard';
    panelBody.appendChild(this.dashboardEl);
    this.renderSessionHero(null);

    this.contextHealthEl = document.createElement('div');
    this.contextHealthEl.id = 'context-health';
    this.contextHealthEl.className = 'rail-card';
    panelBody.appendChild(this.contextHealthEl);
    this.renderContextHealth();

    this.budgetEl = document.createElement('div');
    this.budgetEl.id = 'token-budget-readout';
    this.budgetEl.className = 'rail-card';
    this.budgetEl.innerHTML =
      '<div class="rail-card-label">Next request</div><div class="value mono">—</div><div class="rail-card-note">Estimated tokens vs. the A3 context budget</div>';
    panelBody.appendChild(this.budgetEl);

    this.inspectorEl = document.createElement('details');
    this.inspectorEl.id = 'pre-send-inspector';
    this.inspectorEl.className = 'rail-card';
    this.inspectorEl.innerHTML =
      '<summary>Pre-send inspector</summary><div class="body rail-card-note">Send a message to see the assembled request.</div>';
    panelBody.appendChild(this.inspectorEl);

    const statement = document.createElement('section');
    statement.id = 'pipeline-statement';

    const statementHeader = document.createElement('div');
    statementHeader.className = 'statement-header';
    statementHeader.innerHTML = '<span class="statement-title">Pipeline statement</span>';
    this.statementCountEl = document.createElement('span');
    this.statementCountEl.className = 'statement-count mono';
    statementHeader.appendChild(this.statementCountEl);
    statement.appendChild(statementHeader);

    this.groupsHost = document.createElement('div');
    statement.appendChild(this.groupsHost);

    this.templatesHost = document.createElement('div');
    statement.appendChild(this.templatesHost);

    this.contextTreesHost = document.createElement('div');
    statement.appendChild(this.contextTreesHost);

    panelBody.appendChild(statement);

    this.root.appendChild(panelBody);
  }

  // ---------- Session savings hero ----------

  /** `null` renders the cold-start state — no request has been sent yet, so there is nothing to claim. */
  private renderSessionHero(stats: SessionStats | null) {
    const baseline = stats?.baselineTokensEstimate ?? 0;
    const sent = stats?.tokensSentEstimate ?? 0;
    const saved = Math.max(0, baseline - sent);
    const pct = baseline > 0 ? (saved / baseline) * 100 : 0;
    const hasData = Boolean(stats && stats.requests > 0 && baseline > 0);

    this.dashboardEl.innerHTML = `
      <div class="hero-label">Saved this session</div>
      <div class="hero-value mono${hasData ? '' : ' empty'}">${hasData ? saved.toLocaleString() : '—'}<span class="hero-unit">tok</span></div>
      <div class="hero-bar" role="img" aria-label="${hasData ? `${pct.toFixed(0)}% of the baseline context avoided` : 'No requests yet'}">
        <div class="hero-bar-fill" style="width:${hasData ? pct.toFixed(1) : 0}%"></div>
      </div>
      <div class="hero-meta">
        ${
          hasData
            ? `<span>sent <b class="mono">${sent.toLocaleString()}</b> of <b class="mono">${baseline.toLocaleString()}</b></span><span class="hero-pct">${pct.toFixed(
                0,
              )}% lean</span>`
            : '<span>No requests sent yet this session.</span>'
        }
      </div>
      <div class="hero-stats">
        <div class="stat"><div class="label">Requests</div><div class="value mono">${stats?.requests ?? 0}</div></div>
        <div class="stat"><div class="label">Est. cost</div><div class="value mono">$${(stats?.estimatedCostUsd ?? 0).toFixed(4)}</div></div>
        <div class="stat"><div class="label">In / out</div><div class="value mono">${(stats?.inputTokens ?? 0).toLocaleString()} / ${(
          stats?.outputTokens ?? 0
        ).toLocaleString()}</div></div>
        <div class="stat"><div class="label">Cache read / write</div><div class="value mono">${(
          stats?.cacheReadTokens ?? 0
        ).toLocaleString()} / ${(stats?.cacheWriteTokens ?? 0).toLocaleString()}</div></div>
      </div>`;
  }

  // ---------- Pipeline statement ----------

  private renderGroups() {
    this.groupsHost.innerHTML = '';
    this.groupEls.clear();
    this.groupCountEls.clear();
    this.stageRowEls.clear();
    this.stageSavedEls.clear();

    const byGroup = new Map<StageGroup, PipelineStage[]>();
    for (const stage of PIPELINE_STAGES) {
      const list = byGroup.get(stage.group) ?? [];
      list.push(stage);
      byGroup.set(stage.group, list);
    }

    for (const [group, stages] of byGroup) {
      const groupEl = document.createElement('div');
      groupEl.className = 'opt-group';

      const groupHeader = document.createElement('div');
      groupHeader.className = 'opt-group-header';
      groupHeader.innerHTML = `<span class="group-id mono">${group}</span><span class="group-name">${GROUP_LABELS[group]}</span>`;
      const count = document.createElement('span');
      count.className = 'group-count mono';
      groupHeader.appendChild(count);
      groupHeader.appendChild(
        this.makeSwitch(this.settings.groups[group], (checked) => {
          this.settings.groups[group] = checked;
          this.persist();
          this.syncEffectiveStates();
        }, `Group ${group} master switch`),
      );
      groupEl.appendChild(groupHeader);

      for (const stage of stages) groupEl.appendChild(this.renderStageRow(stage));
      this.groupsHost.appendChild(groupEl);
      this.groupEls.set(group, groupEl);
      this.groupCountEls.set(group, count);
    }

    this.syncEffectiveStates();
  }

  private renderStageRow(stage: PipelineStage): HTMLElement {
    const row = document.createElement('details');
    row.className = `opt-stage impact-${stage.impact.toLowerCase()}`;
    row.dataset.stage = stage.id;

    const summary = document.createElement('summary');
    summary.className = 'opt-stage-row';
    summary.innerHTML = `<span class="stage-id mono">${stage.id}</span><span class="stage-label">${stage.label}</span>`;

    const saved = document.createElement('span');
    saved.className = 'stage-saved mono';
    saved.textContent = '—';
    saved.title = 'Tokens this stage saved on the most recent request';
    summary.appendChild(saved);
    this.stageSavedEls.set(stage.id, saved);

    summary.appendChild(
      this.makeSwitch(this.settings.stages[stage.id] ?? stage.defaultEnabled, (checked) => {
        this.settings.stages[stage.id] = checked;
        this.persist();
        this.syncEffectiveStates();
      }, `${stage.id} ${stage.label}`),
    );
    row.appendChild(summary);

    const body = document.createElement('div');
    body.className = 'opt-stage-body';

    const badges = document.createElement('div');
    badges.className = 'opt-stage-badges';
    badges.innerHTML = `<span class="badge ${stage.impact.toLowerCase()}">${stage.impact} impact</span>${
      stage.implemented ? '' : '<span class="badge stub">not yet implemented</span>'
    }`;
    body.appendChild(badges);

    const desc = document.createElement('div');
    desc.className = 'opt-stage-desc';
    desc.textContent = stage.description;
    body.appendChild(desc);

    if (stage.params) {
      const paramsEl = document.createElement('div');
      paramsEl.className = 'opt-params';
      for (const [key, spec] of Object.entries(stage.params)) {
        const paramRow = document.createElement('div');
        paramRow.className = 'opt-param-row';
        const label = document.createElement('span');
        label.textContent = spec.label;
        paramRow.appendChild(label);

        const currentValue = this.settings.params[stage.id]?.[key] ?? spec.default;

        if (spec.type === 'boolean') {
          paramRow.appendChild(
            this.makeSwitch(Boolean(currentValue), (checked) => {
              this.settings.params[stage.id] = { ...this.settings.params[stage.id], [key]: checked };
              this.persist();
            }, spec.label),
          );
        } else if (spec.type === 'number') {
          const input = document.createElement('input');
          input.type = 'number';
          input.min = String(spec.min);
          input.max = String(spec.max);
          input.value = String(currentValue);
          input.addEventListener('change', () => {
            const num = clamp(Number(input.value) || spec.default, spec.min, spec.max);
            input.value = String(num);
            this.settings.params[stage.id] = { ...this.settings.params[stage.id], [key]: num };
            this.persist();
          });
          paramRow.appendChild(input);
        }
        paramsEl.appendChild(paramRow);
      }
      body.appendChild(paramsEl);
    }

    row.appendChild(body);
    this.stageRowEls.set(stage.id, row);
    return row;
  }

  /**
   * A stage only runs if its group switch AND its own switch are on — both switches keep their own
   * state, so the row says which one is holding it back rather than silently looking enabled.
   */
  private syncEffectiveStates() {
    let enabledTotal = 0;
    for (const stage of PIPELINE_STAGES) {
      const groupOn = this.settings.groups[stage.group] ?? true;
      const stageOn = this.settings.stages[stage.id] ?? stage.defaultEnabled;
      const effective = isStageEffectivelyEnabled(this.settings, stage);
      if (effective) enabledTotal++;

      const row = this.stageRowEls.get(stage.id);
      if (!row) continue;
      row.classList.toggle('stage-off', !effective);
      row.classList.toggle('stage-off-by-group', stageOn && !groupOn);
      row.title = effective
        ? ''
        : stageOn && !groupOn
          ? `Off because group ${stage.group} is switched off`
          : 'Switched off';
    }

    for (const [group, countEl] of this.groupCountEls) {
      const stages = PIPELINE_STAGES.filter((s) => s.group === group);
      const on = stages.filter((s) => isStageEffectivelyEnabled(this.settings, s)).length;
      countEl.textContent = `${on}/${stages.length}`;
      this.groupEls.get(group)?.classList.toggle('group-off', !(this.settings.groups[group] ?? true));
    }

    this.statementCountEl.textContent = `${enabledTotal}/${PIPELINE_STAGES.length} on`;
  }

  // ---------- E3 task templates ----------

  private async loadAndRenderTemplates() {
    this.templates = await getTaskTemplates();
    this.renderTemplates();
  }

  private persistTemplates() {
    void saveTaskTemplates(this.templates);
    this.renderTemplates();
    // The composer renders a chip per template — let it re-read them.
    document.dispatchEvent(new CustomEvent(TEMPLATES_CHANGED_EVENT));
  }

  private renderTemplates() {
    this.templatesHost.innerHTML = '';

    const groupEl = document.createElement('div');
    groupEl.className = 'opt-group';

    const groupHeader = document.createElement('div');
    groupHeader.className = 'opt-group-header';
    groupHeader.innerHTML = '<span class="group-id mono">E3</span><span class="group-name">Task templates</span>';
    groupEl.appendChild(groupHeader);

    const desc = document.createElement('div');
    desc.className = 'opt-stage-desc templates-desc';
    desc.textContent = 'Typing one of these as the first word of a message expands it into the instruction below before sending.';
    groupEl.appendChild(desc);

    for (const [command, expansion] of Object.entries(this.templates)) {
      groupEl.appendChild(this.renderTemplateRow(command, expansion));
    }
    groupEl.appendChild(this.renderNewTemplateRow());

    this.templatesHost.appendChild(groupEl);
  }

  private renderTemplateRow(command: string, expansion: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'opt-template-row';

    const top = document.createElement('div');
    top.className = 'opt-template-top';
    const label = document.createElement('code');
    label.textContent = command;
    top.appendChild(label);
    const removeBtn = document.createElement('button');
    removeBtn.className = 'btn subtle';
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', () => {
      delete this.templates[command];
      this.persistTemplates();
    });
    top.appendChild(removeBtn);
    row.appendChild(top);

    const textarea = document.createElement('textarea');
    textarea.value = expansion;
    textarea.rows = 2;
    textarea.addEventListener('change', () => {
      this.templates[command] = textarea.value;
      this.persistTemplates();
    });
    row.appendChild(textarea);

    return row;
  }

  private renderNewTemplateRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'opt-template-row new';

    const commandInput = document.createElement('input');
    commandInput.type = 'text';
    commandInput.placeholder = '/command';

    const textArea = document.createElement('textarea');
    textArea.rows = 2;
    textArea.placeholder = 'Expansion instruction sent in place of the command';

    const addBtn = document.createElement('button');
    addBtn.className = 'btn subtle';
    addBtn.textContent = 'Add template';
    addBtn.addEventListener('click', () => {
      const command = commandInput.value.trim();
      const expansion = textArea.value.trim();
      if (!command.startsWith('/') || !expansion) {
        alert('Enter a command starting with "/" and a non-empty expansion.');
        return;
      }
      this.templates[command] = expansion;
      this.persistTemplates();
    });

    row.appendChild(commandInput);
    row.appendChild(textArea);
    row.appendChild(addBtn);
    return row;
  }

  // ---------- ContextTrees (agent mode project context) ----------

  private async loadAndRenderContextTrees() {
    this.contextTrees = await getContextTreesSettings();
    this.renderContextTrees();
  }

  private persistContextTrees() {
    void saveContextTreesSettings(this.contextTrees);
  }

  /**
   * Deliberately opt-in and manual by default: ContextTrees adds a system-context block to every
   * request (and an extra API call in `llm` authoring mode), which cuts against this panel's entire
   * subject. It sits here, under the statement, so the cost is stated where savings are.
   */
  private renderContextTrees() {
    this.contextTreesHost.innerHTML = '';

    const groupEl = document.createElement('div');
    groupEl.className = 'opt-group';

    const rows = document.createElement('div');
    rows.className = 'ct-settings-rows';
    rows.classList.toggle('ct-settings-off', !this.contextTrees.enabled);

    const groupHeader = document.createElement('div');
    groupHeader.className = 'opt-group-header';
    groupHeader.innerHTML = '<span class="group-id mono">CT</span><span class="group-name">ContextTrees</span>';
    groupHeader.appendChild(
      this.makeSwitch(this.contextTrees.enabled, (checked) => {
        this.contextTrees.enabled = checked;
        this.persistContextTrees();
        rows.classList.toggle('ct-settings-off', !checked);
      }, 'ContextTrees enabled'),
    );
    groupEl.appendChild(groupHeader);

    const desc = document.createElement('div');
    desc.className = 'opt-stage-desc templates-desc';
    desc.textContent =
      'Scopes agent-mode sessions to a project design map, adding a context block per request — off by default because that costs tokens. Review decides what a finished session writes back.';
    groupEl.appendChild(desc);

    rows.appendChild(
      this.makeSelectRow('Design map review', this.contextTrees.reviewMode.designMap, [
        ['manual', 'Manual popup'],
        ['auto', 'Auto commit'],
      ], (value) => {
        this.contextTrees.reviewMode.designMap = value as 'auto' | 'manual';
        this.persistContextTrees();
      }),
    );

    rows.appendChild(
      this.makeSelectRow('Agent memory review', this.contextTrees.reviewMode.agentMemory, [
        ['manual', 'Manual popup'],
        ['auto', 'Auto commit'],
      ], (value) => {
        this.contextTrees.reviewMode.agentMemory = value as 'auto' | 'manual';
        this.persistContextTrees();
      }),
    );

    rows.appendChild(
      this.makeSelectRow('Update authoring', this.contextTrees.updateAuthoring, [
        ['heuristic', 'Heuristic (free)'],
        ['llm', 'LLM (extra call)'],
      ], (value) => {
        this.contextTrees.updateAuthoring = value as 'llm' | 'heuristic';
        this.persistContextTrees();
      }),
    );

    groupEl.appendChild(rows);
    this.contextTreesHost.appendChild(groupEl);
  }

  // ---------- Shared controls ----------

  private makeSelectRow(
    label: string,
    value: string,
    options: [value: string, label: string][],
    onChange: (value: string) => void,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'opt-param-row';

    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    row.appendChild(labelEl);

    const select = document.createElement('select');
    select.setAttribute('aria-label', label);
    for (const [optionValue, optionLabel] of options) {
      const option = document.createElement('option');
      option.value = optionValue;
      option.textContent = optionLabel;
      select.appendChild(option);
    }
    select.value = value;
    select.addEventListener('change', () => onChange(select.value));
    row.appendChild(select);

    return row;
  }

  /** Thin alias over the shared control so the rail's Project Mode switch can't visually drift from these. */
  private makeSwitch(checked: boolean, onChange: (checked: boolean) => void, label?: string): HTMLLabelElement {
    return createSwitch(checked, onChange, label);
  }

  private persist() {
    void saveOptimizationSettings(this.settings);
    this.callbacks.onSettingsChanged(this.settings);
  }

  private async resetToRecommended() {
    this.settings = await resetOptimizationSettingsToRecommended();
    this.renderGroups();
    this.callbacks.onSettingsChanged(this.settings);
  }

  getSettings(): OptimizationSettings {
    return this.settings;
  }

  // ---------- Live readouts ----------

  updateTokenBudgetReadout(estimatedTokens: number, budget: number) {
    const over = estimatedTokens > budget;
    this.budgetEl.classList.toggle('over', over);
    this.budgetEl.innerHTML = `
      <div class="rail-card-label">Next request</div>
      <div class="value mono">${estimatedTokens.toLocaleString()} <span class="of">/ ${budget.toLocaleString()}</span></div>
      <div class="rail-card-note">Estimated tokens for the next request vs. the A3 context budget</div>`;
  }

  /** Called after each send with the *actual* total request size (report.totalTokensAfter). */
  updateContextHealth(totalTokens: number, model: string) {
    this.lastContextTokens = totalTokens;
    this.renderContextHealth(model);
  }

  private renderContextHealth(model = 'claude-sonnet-5') {
    const softLimit = this.settings.contextSoftLimitTokens;
    const windowSize = contextWindowFor(model);
    const totalTokens = this.lastContextTokens;
    const pctOfSoft = softLimit > 0 ? Math.min(100, (totalTokens / softLimit) * 100) : 0;

    let status: 'ok' | 'warn' | 'over' = 'ok';
    if (totalTokens > softLimit) status = 'over';
    else if (totalTokens > softLimit * 0.7) status = 'warn';

    this.contextHealthEl.dataset.status = status;
    this.contextHealthEl.innerHTML = `
      <div class="rail-card-label">Context health</div>
      <div class="value mono">${totalTokens.toLocaleString()} <span class="of">tok</span></div>
      <div class="context-health-bar"><div class="context-health-bar-fill" style="width:${pctOfSoft}%"></div></div>
      <div class="rail-card-note">
        Soft warning threshold ${softLimit.toLocaleString()} tok · model window ${windowSize.toLocaleString()} tok.
        The threshold is a heuristic, not a published limit — quality can degrade well before the hard window fills up.
      </div>
      <div class="opt-param-row">
        <span>Soft warning threshold</span>
        <input type="number" id="context-soft-limit-input" min="5000" max="500000" step="1000" value="${softLimit}" />
      </div>
      <button class="btn subtle" id="trim-context-btn">Trim context</button>
    `;

    const input = this.contextHealthEl.querySelector<HTMLInputElement>('#context-soft-limit-input')!;
    input.addEventListener('change', () => {
      const value = clamp(Number(input.value) || DEFAULT_CONTEXT_SOFT_LIMIT_TOKENS, 5000, 500000);
      input.value = String(value);
      this.settings.contextSoftLimitTokens = value;
      this.persist();
      this.renderContextHealth(model);
    });

    const trimBtn = this.contextHealthEl.querySelector<HTMLButtonElement>('#trim-context-btn')!;
    trimBtn.title = 'Close the least-recently-touched open files to bring context size back down';
    trimBtn.addEventListener('click', () => this.callbacks.onTrimContext());
  }

  updateSessionDashboard(stats: SessionStats) {
    this.renderSessionHero(stats);
  }

  updatePreSendInspector(report: PipelineRunReport, requestBody: AnthropicRequestBody, usage?: UsageInfo) {
    this.applyStageSavings(report);

    const stageRows = report.stages
      .map((s) => {
        const savedText = s.report ? `saved ~${s.report.tokensSaved.toLocaleString()} tok` : s.skippedReason ?? 'skipped';
        return `<div class="stage-report"><span class="mono">${s.stageId}${s.ran ? '' : ' (skipped)'}</span><span>${savedText}</span></div>`;
      })
      .join('');

    const reconciled = usage
      ? `<div class="stage-report"><span>Actual (from API)</span><span class="mono">in ${usage.input_tokens ?? 0} / out ${
          usage.output_tokens ?? 0
        } / cache-read ${usage.cache_read_input_tokens ?? 0}</span></div>`
      : '';

    const wasOpen = this.inspectorEl.open;
    this.inspectorEl.innerHTML = `
      <summary>Pre-send inspector</summary>
      <div class="body">
        <div class="stage-report"><span>Estimated before pipeline</span><span class="mono">${report.totalTokensBefore.toLocaleString()} tok</span></div>
        <div class="stage-report"><span>Estimated after pipeline</span><span class="mono">${report.totalTokensAfter.toLocaleString()} tok</span></div>
        ${stageRows}
        ${reconciled}
        ${report.timedOut ? '<div class="stage-report"><span>⚠ 2s budget exceeded</span><span>remaining stages skipped</span></div>' : ''}
        <pre>${escapeHtml(JSON.stringify(requestBody, null, 2))}</pre>
      </div>`;
    this.inspectorEl.open = wasOpen;
  }

  /** Mirrors the run's per-stage savings onto the statement rows. Last run only — never a running total. */
  private applyStageSavings(report: PipelineRunReport) {
    this.lastStageSaved = {};
    for (const stage of report.stages) {
      if (stage.report) this.lastStageSaved[stage.stageId] = stage.report.tokensSaved;
    }
    for (const [stageId, el] of this.stageSavedEls) {
      const saved = this.lastStageSaved[stageId];
      if (saved === undefined) {
        el.textContent = '—';
        el.classList.remove('positive');
        continue;
      }
      el.textContent = saved > 0 ? `−${saved.toLocaleString()}` : '0';
      el.classList.toggle('positive', saved > 0);
    }
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
